import moment from "moment"
import {
  audienceOptions,
  contentPurposeOptions,
  resourceFeatureOptions,
} from "../../Resource/criteriaOptions"
import { optionLabel } from "../../Opportunity/criteriaOptions"
import { FILTER_DATE_FORMAT } from "../../../helpers/dateHandlers"
import { options as industryOptions } from "../MemberSearchFilter/wrappers/CompanyIndustryTypeDropdown/options"
import { options as sectorOptions } from "../MemberSearchFilter/wrappers/CompanyRoleTypeDropdown/options"
import { options as resourceTypeOptions } from "./wrappers/ResourceTypeDropdown/options"
import { options as eventTypeOptions } from "./wrappers/EventTypeDropdown/options"
import { options as accessTypeOptions } from "./wrappers/AccessTypeDropdown/options"

// Core and Advanced filters are mutually exclusive: only the active mode's
// values are ever sent to the API.
export const CORE = "core"
export const ADVANCED = "advanced"
export const MODE_PARAM = "filter_mode"
const DATE_FORMAT = "YYYY-MM-DD"

const withAll = (label, options) => [
  { id: "", value: label },
  ...options.filter((option) => option.id !== ""),
]

export const filterOptions = {
  // Existing values reused as they are
  resourceType: withAll("All Types", resourceTypeOptions),
  eventType: withAll("All Types", eventTypeOptions),
  accessType: withAll("All Resources", accessTypeOptions),
  industry: withAll("All Industries", industryOptions),
  sector: sectorOptions.filter((option) => option.id !== ""),
  // Audience, content purpose and features come from the design document
  audience: audienceOptions,
  contentPurpose: contentPurposeOptions,
  resourceFeatures: resourceFeatureOptions,
  resourceFormat: [
    { id: "", value: "All Formats" },
    { id: "in_person", value: "In-person" },
    { id: "virtual", value: "Virtual" },
  ],
  cost: [
    { id: "", value: "All Costs" },
    { id: "free", value: "Free" },
    { id: "cost", value: "Cost" },
  ],
}

// `kind`: "text" (default), "list" (comma separated) or "date" (moment <-> YYYY-MM-DD).
// `options` is used to label the active filter tags.
export const FILTER_FIELDS = {
  [CORE]: [
    {
      name: "resourceType",
      param: "resource_type",
      options: filterOptions.resourceType,
    },
    {
      name: "audience",
      param: "audience",
      kind: "list",
      options: filterOptions.audience,
    },
    { name: "keywords", param: "keywords", tag: "Keywords" },
    {
      name: "resourceFormat",
      param: "resource_format",
      options: filterOptions.resourceFormat,
    },
    {
      name: "accessType",
      param: "event_type",
      options: filterOptions.accessType,
      tag: "Access",
    },
    { name: "fromDate", param: "from", kind: "date", tag: "From" },
    { name: "toDate", param: "to", kind: "date", tag: "To" },
  ],
  [ADVANCED]: [
    {
      name: "sector",
      param: "describe_company",
      kind: "list",
      options: filterOptions.sector,
    },
    { name: "industry", param: "industry", options: filterOptions.industry },
    {
      name: "eventType",
      param: "resource_sub_type",
      options: filterOptions.eventType,
      tag: "Event type",
    },
    { name: "location", param: "location", tag: "Location" },
    {
      name: "contentPurpose",
      param: "content_purpose",
      kind: "list",
      options: filterOptions.contentPurpose,
    },
    {
      name: "cost",
      param: "cost",
      options: filterOptions.cost,
      tag: "Cost",
    },
    {
      name: "resourceFeatures",
      param: "resource_features",
      kind: "list",
      options: filterOptions.resourceFeatures,
    },
  ],
}

// The source/provider company is picked in the Advanced filters.
const COMPANY_PARAMS = ["company", "company_name"]

export const MODE_PARAMS = {
  [CORE]: FILTER_FIELDS[CORE].map(({ param }) => param),
  [ADVANCED]: [
    ...FILTER_FIELDS[ADVANCED].map(({ param }) => param),
    ...COMPANY_PARAMS,
  ],
}

export const ALL_FILTER_PARAMS = [
  ...MODE_PARAMS[CORE],
  ...MODE_PARAMS[ADVANCED],
  MODE_PARAM,
]

// Params that depend on another one and are removed together with it.
const DEPENDENT_PARAMS = {
  company: ["company_name"],
}

const emptyValue = ({ kind }) => {
  if (kind === "list") return []
  if (kind === "date") return null
  return ""
}

export const initialValues = {
  [CORE]: Object.fromEntries(
    FILTER_FIELDS[CORE].map((field) => [field.name, emptyValue(field)]),
  ),
  [ADVANCED]: {
    ...Object.fromEntries(
      FILTER_FIELDS[ADVANCED].map((field) => [field.name, emptyValue(field)]),
    ),
    company: null,
    companyInput: "",
  },
}

export const allInitialValues = {
  ...initialValues[CORE],
  ...initialValues[ADVANCED],
}

const hasValue = (value) =>
  Array.isArray(value)
    ? value.length > 0
    : value !== null && value !== undefined && value !== ""

const toParam = ({ kind }, value) => {
  if (kind === "list") return value.join(",")
  if (kind === "date") return moment(value).format(DATE_FORMAT)
  return String(value).trim()
}

const fromParam = ({ kind }, value) => {
  if (kind === "list") return String(value).split(",").filter(Boolean)
  if (kind === "date") return moment(value, DATE_FORMAT)
  return value
}

/** Panel values of the active mode -> URL/API search params. */
export const toSearchParams = (mode, values) => {
  const params = {}
  FILTER_FIELDS[mode].forEach((field) => {
    const value = values[field.name]
    if (!hasValue(value)) return
    const param = toParam(field, value)
    if (param === "") return
    params[field.param] = param
  })
  if (mode === ADVANCED && values.company) {
    params.company = values.company.id
    params.company_name = values.company.name
  }
  if (mode === ADVANCED) params[MODE_PARAM] = ADVANCED
  return params
}

/** Number of filters applied in the panel; every list item counts once. */
export const countAppliedFilters = (mode, values) => {
  const params = toSearchParams(mode, values)
  return (
    FILTER_FIELDS[mode].reduce((count, { param, kind }) => {
      if (!(param in params)) return count
      return count + (kind === "list" ? params[param].split(",").length : 1)
    }, 0) + (params.company ? 1 : 0)
  )
}

const hasModeParams = (mode, searchParamGroup) =>
  MODE_PARAMS[mode].some((param) => hasValue(searchParamGroup[param]))

/** URL search params -> {mode, values}. `mode` is null when no filter is applied. */
export const fromSearchParams = (searchParamGroup = {}) => {
  let mode = null
  if (searchParamGroup[MODE_PARAM] === ADVANCED) mode = ADVANCED
  else if (hasModeParams(CORE, searchParamGroup)) mode = CORE
  else if (hasModeParams(ADVANCED, searchParamGroup)) mode = ADVANCED

  const values = { ...allInitialValues }
  ;[...FILTER_FIELDS[CORE], ...FILTER_FIELDS[ADVANCED]].forEach((field) => {
    const value = searchParamGroup[field.param]
    if (hasValue(value)) values[field.name] = fromParam(field, value)
  })
  if (searchParamGroup.company) {
    values.company = {
      id: searchParamGroup.company,
      name: searchParamGroup.company_name,
    }
    values.companyInput = searchParamGroup.company_name || ""
  }
  return { mode, values }
}

export const isFilterApplied = (searchParamGroup = {}) =>
  ALL_FILTER_PARAMS.filter((param) => param !== MODE_PARAM).some((param) =>
    hasValue(searchParamGroup[param]),
  )

export const omitFilterParams = (
  searchParamGroup,
  params = ALL_FILTER_PARAMS,
) =>
  Object.fromEntries(
    Object.entries(searchParamGroup).filter(([key]) => !params.includes(key)),
  )

/** Removes one applied filter (or one value of a list filter) from the search params. */
export const removeFilterParam = (searchParamGroup, param, value) => {
  const field = [...FILTER_FIELDS[CORE], ...FILTER_FIELDS[ADVANCED]].find(
    (item) => item.param === param,
  )
  if (field?.kind === "list" && value) {
    const remaining = fromParam(field, searchParamGroup[param]).filter(
      (item) => item !== value,
    )
    if (remaining.length)
      return { ...searchParamGroup, [param]: remaining.join(",") }
  }
  return omitFilterParams(searchParamGroup, [
    param,
    ...(DEPENDENT_PARAMS[param] || []),
  ])
}

const formatTagValue = (field, value) => {
  if (field.kind === "date")
    return moment(value, DATE_FORMAT).format(FILTER_DATE_FORMAT)
  return optionLabel(field.options || [], value) || value
}

/** Active filter tags shown above the results: one tag per value. */
export const buildFilterTags = (searchParamGroup = {}) => {
  const tags = []
  ;[...FILTER_FIELDS[CORE], ...FILTER_FIELDS[ADVANCED]].forEach((field) => {
    const raw = searchParamGroup[field.param]
    if (!hasValue(raw)) return
    const values = field.kind === "list" ? fromParam(field, raw) : [raw]
    values.forEach((value) => {
      const label = formatTagValue(field, value)
      tags.push({
        key: `${field.param}-${value}`,
        param: field.param,
        value: field.kind === "list" ? value : undefined,
        title: field.tag ? `${field.tag}: ${label}` : label,
      })
    })
  })
  if (hasValue(searchParamGroup.company_name)) {
    tags.push({
      key: "company",
      param: "company",
      title: `Source: ${searchParamGroup.company_name}`,
    })
  }
  return tags
}
